import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { apiService } from '../services/apiService';
import { ROUTES } from '../config/routes';
import Logo from '../components/Logo';

const DEPARTMENTS = ['Engineering', 'IT', 'Operations', 'Marketing', 'HR'];
const SHIFTS = ['MORNING', 'EVENING', 'NIGHT'] as const;

const TEST_DURATIONS = [
  { value: 1, label: '1 Minute (Quick Test)' },
  { value: 3, label: '3 Minutes (Short)' },
  { value: 5, label: '5 Minutes (Standard)' },
  { value: 10, label: '10 Minutes (Full Assessment)' }
] as const;

const KioskStart: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    kioskToken: '',
    employeeId: '',
    employeeName: '',
    department: '',
    workShift: 'MORNING' as (typeof SHIFTS)[number],
    lastSleepDuration: '6',
    sleepQuality: '3',
    sleepinessLevel: '4',
    testDurationMinutes: 5,
    testType: 'CHECKERBOARD' as 'CHECKERBOARD' | 'LETTER',
    isMockup: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (!formData.kioskToken.trim()) {
        throw new Error('Kiosk token is required.');
      }
      if (!TEST_DURATIONS.some(({ value }) => value === formData.testDurationMinutes)) {
        throw new Error('Choose one of the available test durations.');
      }
      const response = await apiService.startKioskTest({
        kioskToken: formData.kioskToken.trim().toUpperCase(),
        employeeId: formData.employeeId.trim(),
        employeeName: formData.employeeName.trim() || undefined,
        department: formData.department.trim(),
        workShift: formData.workShift,
        lastSleepDuration: Number(formData.lastSleepDuration),
        sleepQuality: Number(formData.sleepQuality),
        sleepinessLevel: Number(formData.sleepinessLevel),
        testDurationMinutes: formData.testDurationMinutes,
        testType: formData.testType
      });

      if (!response.success || !response.data?.testId) {
        throw new Error(response.error?.message || 'Unable to start kiosk test');
      }

      sessionStorage.setItem(
        'kioskSession',
        JSON.stringify({
          companyCode: response.data.company?.code,
          companyId: response.data.company?.id,
          kioskToken: formData.kioskToken.trim().toUpperCase(),
          employeeId: formData.employeeId.trim(),
          employeeName: formData.employeeName.trim(),
          department: formData.department.trim(),
          workShift: formData.workShift,
          testDurationMinutes: formData.testDurationMinutes,
          testType: formData.testType,
          isMockup: formData.isMockup
        })
      );

      navigate(ROUTES.KIOSK.TEST(response.data.testId));
    } catch (err: any) {
      setError(err?.message || 'Failed to start kiosk test');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl bg-navy-800 border border-navy-700 rounded-2xl shadow-xl p-8">
        <div className="text-center mb-6">
          <div className="flex justify-center mb-4">
            <Logo size="md" />
          </div>
          <h1 className="text-2xl font-bold text-white">Start a Fatigue Check</h1>
          <p className="text-sm text-navy-200 mt-2">
            Enter your details to begin the fatigue test.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="kioskToken" className="text-sm font-medium text-navy-100">Kiosk Token</label>
            <input
              id="kioskToken"
              value={formData.kioskToken}
              onChange={(e) => handleChange('kioskToken', e.target.value.toUpperCase())}
              className="mt-1 w-full rounded-lg border border-navy-700 bg-navy-900 text-navy-50 placeholder-navy-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500 tracking-widest uppercase"
              placeholder="6 characters"
              maxLength={6}
              required
            />
            <p className="text-xs text-navy-300 mt-1">
              The token identifies your company - no company code needed.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="employeeId" className="text-sm font-medium text-navy-100">Employee ID</label>
              <input
                id="employeeId"
                value={formData.employeeId}
                onChange={(e) => handleChange('employeeId', e.target.value)}
                className="mt-1 w-full rounded-lg border border-navy-700 bg-navy-900 text-navy-50 placeholder-navy-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                placeholder="EMP-001"
                required
              />
            </div>
            <div>
              <label htmlFor="testDuration" className="text-sm font-medium text-navy-100">Test Duration</label>
              <select
                id="testDuration"
                value={formData.testDurationMinutes}
                onChange={(e) => setFormData((prev) => ({ ...prev, testDurationMinutes: Number(e.target.value) }))}
                className="mt-1 w-full rounded-lg border border-navy-700 bg-navy-900 text-navy-50 placeholder-navy-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                required
              >
                {TEST_DURATIONS.map((duration) => (
                  <option key={duration.value} value={duration.value}>
                    {duration.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="testType" className="text-sm font-medium text-navy-100">Test Type</label>
              <select
                id="testType"
                value={formData.testType}
                onChange={(e) => handleChange('testType', e.target.value)}
                className="mt-1 w-full rounded-lg border border-navy-700 bg-navy-900 text-navy-50 placeholder-navy-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                required
              >
                <option value="CHECKERBOARD">Checkerboard Pattern (Standard)</option>
                <option value="LETTER">Letter Recall (Memory)</option>
              </select>
            </div>
          </div>

          <label className="flex min-h-11 items-start gap-3 text-sm text-navy-100">
            <input
              type="checkbox"
              checked={formData.isMockup}
              onChange={(e) => setFormData((prev) => ({ ...prev, isMockup: e.target.checked }))}
              className="mt-0.5 text-gold-400 focus:ring-gold-500"
            />
            <span>
              <span className="font-medium">Mockup Test (30 seconds)</span>
              <span className="block text-xs text-navy-300">
                Runs a 30-second practice before the actual test. Mockup results are not saved.
              </span>
            </span>
          </label>

          <div>
            <label htmlFor="employeeName" className="text-sm font-medium text-navy-100">Employee Name (optional)</label>
            <input
              id="employeeName"
              value={formData.employeeName}
              onChange={(e) => handleChange('employeeName', e.target.value)}
              className="mt-1 w-full rounded-lg border border-navy-700 bg-navy-900 text-navy-50 placeholder-navy-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              placeholder="Full name"
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="department" className="text-sm font-medium text-navy-100">Department</label>
              <select
                id="department"
                value={formData.department}
                onChange={(e) => handleChange('department', e.target.value)}
                className="mt-1 w-full rounded-lg border border-navy-700 bg-navy-900 text-navy-50 placeholder-navy-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                required
              >
                <option value="">Select department</option>
                {DEPARTMENTS.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="workShift" className="text-sm font-medium text-navy-100">Shift</label>
              <select
                id="workShift"
                value={formData.workShift}
                onChange={(e) => handleChange('workShift', e.target.value)}
                className="mt-1 w-full rounded-lg border border-navy-700 bg-navy-900 text-navy-50 placeholder-navy-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              >
                {SHIFTS.map((shift) => (
                  <option key={shift} value={shift}>
                    {shift}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="lastSleepDuration" className="text-sm font-medium text-navy-100">Last Sleep (hrs)</label>
              <input
                id="lastSleepDuration"
                type="number"
                min="0"
                step="0.1"
                value={formData.lastSleepDuration}
                onChange={(e) => handleChange('lastSleepDuration', e.target.value)}
                className="mt-1 w-full rounded-lg border border-navy-700 bg-navy-900 text-navy-50 placeholder-navy-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
                required
              />
            </div>
            <div>
              <label htmlFor="sleepQuality" className="text-sm font-medium text-navy-100">Sleep Quality (1-4)</label>
              <select
                id="sleepQuality"
                value={formData.sleepQuality}
                onChange={(e) => handleChange('sleepQuality', e.target.value)}
                className="mt-1 w-full rounded-lg border border-navy-700 bg-navy-900 text-navy-50 placeholder-navy-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              >
                <option value="1">1 - Poor</option>
                <option value="2">2 - Fair</option>
                <option value="3">3 - Good</option>
                <option value="4">4 - Excellent</option>
              </select>
            </div>
            <div>
              <label htmlFor="sleepinessLevel" className="text-sm font-medium text-navy-100">Sleepiness (1-9)</label>
              <select
                id="sleepinessLevel"
                value={formData.sleepinessLevel}
                onChange={(e) => handleChange('sleepinessLevel', e.target.value)}
                className="mt-1 w-full rounded-lg border border-navy-700 bg-navy-900 text-navy-50 placeholder-navy-400 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-500"
              >
                {[1,2,3,4,5,6,7,8,9].map((level) => (
                  <option key={level} value={String(level)}>{level}</option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/40 text-red-400 text-sm px-4 py-2">
              {error}
            </div>
          )}

          <Button
            type="submit"
            className="w-full bg-gold-500 hover:bg-gold-600"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Starting Test...' : 'Start Test'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default KioskStart;
